import classNames from 'classnames';

import styles from './Pagination.module.scss';
import { useSitecoreContext } from 'hooks/useSitecoreContext';

type Props = {
  currentPage: number;
  maxPages: number;
  isLoading: boolean;
  showSkeleton?: boolean;
  className: string;
  onSelect: (page: number) => void;
};

const Pagination = ({
  currentPage,
  maxPages,
  showSkeleton,
  isLoading,
  className,
  onSelect,
}: Props) => {
  const { sitecoreContext } = useSitecoreContext();
  const siteTheme = sitecoreContext.pageTheme?.toLowerCase() || 'master';
  const getPage = (page: number) => {
    if (maxPages <= 5 && page <= maxPages) {
      return page;
    }
    if (currentPage === 1) {
      return page;
    }
    if (page === 1) {
      return currentPage > maxPages - 2 ? maxPages - 3 : currentPage - 1;
    } else if (page === 2) {
      return currentPage > maxPages - 2 ? maxPages - 2 : currentPage;
    } else {
      return currentPage > maxPages - 2 ? maxPages - 1 : currentPage + 1;
    }
  };

  return (
    <div
      className={classNames('d-flex align-items-center', {
        [styles['page-loading']]: isLoading,
        [className]: true,
      })}
    >
      <>
        {!showSkeleton && (
          <>
            <em
              className={classNames('bi-chevron-left me-2', styles.tile, styles[siteTheme], {
                [styles.disabled]: !(currentPage > 1),
              })}
              tabIndex={0}
              onClick={() => currentPage > 1 && onSelect(currentPage - 1)}
              onKeyDown={(e) => {
                if (e.code === 'Enter' || e.code === 'Space') {
                  if (currentPage > 1) {
                    onSelect(currentPage - 1);
                  }
                }
              }}
            />
            {maxPages > 5 && currentPage >= maxPages - 2 && currentPage <= maxPages && (
              <div className={`me-2 ${styles.dots}`}>...</div>
            )}
            {[...Array(maxPages > 5 ? 3 : maxPages)].map((_, i) => (
              <div
                key={i + 'pages'}
                className={classNames('me-2', styles.tile, styles[siteTheme], {
                  [styles.selected]: getPage(i + 1) === currentPage,
                })}
                tabIndex={0}
                onClick={() => onSelect(getPage(i + 1))}
                onKeyDown={(e) => {
                  if (e.code === 'Enter' || e.code === 'Space') {
                    onSelect(getPage(i + 1));
                  }
                }}
              >
                <>{getPage(i + 1)}</>
              </div>
            ))}
            {maxPages > 5 && currentPage < maxPages - 2 && (
              <div className={`me-2 ${styles.dots}`}>...</div>
            )}
            {maxPages > 5 && (
              <div
                tabIndex={0}
                className={classNames('me-2', styles.tile, styles[siteTheme], {
                  [styles.selected]: currentPage === maxPages,
                })}
                onClick={() => onSelect(maxPages)}
                onKeyDown={(e) => {
                  if (e.code === 'Enter' || e.code === 'Space') {
                    onSelect(maxPages);
                  }
                }}
              >
                {maxPages}
              </div>
            )}
            <em
              className={classNames('bi-chevron-right', styles.tile, styles[siteTheme], {
                [styles.disabled]: !(currentPage < maxPages),
              })}
              tabIndex={0}
              onClick={() => currentPage < maxPages && onSelect(currentPage + 1)}
              onKeyDown={(e) => {
                if (e.code === 'Enter' || e.code === 'Space') {
                  if (currentPage < maxPages) {
                    onSelect(currentPage + 1);
                  }
                }
              }}
            />
          </>
        )}
      </>
    </div>
  );
};

export default Pagination;
